import type { ReactNode } from 'react';
import { useState, useEffect, useCallback } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import type { Book, ExternalBook } from '@/api/types';
import { searchBooks, externalSearchBooks } from '@/api/books';

const DEBOUNCE_MS = 800;

type Option =
  | { type: 'local'; book: Book }
  | { type: 'external'; book: ExternalBook }
  | { type: 'create'; title: string }
  | { type: 'external_error'; message: string };

function getOptionLabel(opt: string | Option): string {
  if (typeof opt === 'string') return opt;
  if (opt.type === 'local') return opt.book.title;
  if (opt.type === 'external') return opt.book.title;
  // Для опции «создать» возвращаем только название — в инпут подставится именно оно
  if (opt.type === 'create') return opt.title;
  if (opt.type === 'external_error') return opt.message;
  return '';
}

/** В списке опций показываем «название — автор», но без «—», если автора нет */
function getOptionDisplayLabel(option: Option): string {
  if (option.type === 'external_error') return option.message;
  if (option.type === 'local') {
    const authorName = option.book.author?.name?.trim();
    return authorName ? `${option.book.title} — ${authorName}` : option.book.title;
  }
  if (option.type === 'external') {
    const authorName = option.book.author?.trim();
    return authorName ? `${option.book.title} — ${authorName}` : option.book.title;
  }
  return `Создать новую книгу: "${option.title}"`;
}

export interface BookTitleAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelectLocalBook: (book: Book) => void;
  onSelectExternalBook: (book: ExternalBook) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

export function BookTitleAutocomplete({
  value,
  onChange,
  onSelectLocalBook,
  onSelectExternalBook,
  disabled = false,
  error = false,
  helperText,
}: BookTitleAutocompleteProps): ReactNode {
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [debouncedInput, setDebouncedInput] = useState(value);
  const [hasTyped, setHasTyped] = useState(false);
  const [externalSearchError, setExternalSearchError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedInput(value), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [value]);

  useEffect(() => {
    if (!hasTyped) {
      // Не инициируем поиск до тех пор, пока пользователь ничего не ввёл
      return;
    }
    const query = debouncedInput.trim();
    if (!query) {
      setOptions([]);
      setExternalSearchError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setExternalSearchError(null);

    void (async () => {
      const [localSettled, externalSettled] = await Promise.allSettled([
        searchBooks(query, 10),
        externalSearchBooks(query),
      ]);

      if (cancelled) return;

      const localBooks = localSettled.status === 'fulfilled' ? localSettled.value : [];
      const localOptions: Option[] = localBooks.map((book) => ({ type: 'local' as const, book }));

      let externalOptions: Option[] = [];
      let extError: string | null = null;
      if (externalSettled.status === 'fulfilled') {
        externalOptions = externalSettled.value.map((book) => ({ type: 'external' as const, book }));
      } else {
        const reason = externalSettled.reason;
        extError =
          reason instanceof Error
            ? reason.message
            : 'Не удалось выполнить внешний поиск';
      }

      setOptions([...localOptions, ...externalOptions]);
      setExternalSearchError(extError);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedInput, hasTyped]);

  const handleChange = useCallback(
    (_event: unknown, newValue: string | Option | null) => {
      if (!newValue) return;
      if (typeof newValue === 'object' && newValue.type === 'external_error') return;
      if (typeof newValue === 'string') {
        // Просто оставляем введённый текст, создание произойдёт по кнопке \"Сохранить\"
        onChange(newValue);
        return;
      }
      if (newValue.type === 'create') {
        onChange(newValue.title);
        return;
      }
      if (newValue.type === 'local') {
        onSelectLocalBook(newValue.book);
      } else if (newValue.type === 'external') {
        onSelectExternalBook(newValue.book);
      }
    },
    [onChange, onSelectLocalBook, onSelectExternalBook]
  );

  const computedOptions: Option[] = (() => {
    const trimmed = value.trim();
    const withError: Option[] =
      externalSearchError != null
        ? [...options, { type: 'external_error' as const, message: externalSearchError }]
        : options;
    if (!trimmed) return withError;
    // Всегда показывать "Создать новую книгу", когда есть ввод и есть хоть какие-то варианты (или только создать)
    return [...withError, { type: 'create', title: trimmed }];
  })();

  return (
    <Autocomplete
      freeSolo
      inputValue={value}
      onInputChange={(_event, newInputValue) => {
        setHasTyped(true);
        onChange(newInputValue);
      }}
      options={computedOptions}
      getOptionLabel={getOptionLabel}
      onChange={handleChange}
      disabled={disabled}
      noOptionsText="Нет вариантов"
      filterOptions={(x) => x}
      getOptionDisabled={(option) =>
        typeof option === 'object' && option !== null && option.type === 'external_error'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Название книги"
          fullWidth
          disabled={disabled}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        if (option.type === 'external_error') {
          const { key: liKey, ...liProps } = props as typeof props & { key?: string };
          return (
            <li
              key={liKey ?? 'external-search-error'}
              {...liProps}
              style={{
                display: 'block',
                cursor: 'default',
                listStyle: 'none',
                padding: '8px 12px',
              }}
              onMouseDown={(e) => e.preventDefault()}
            >
              <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>
                Внешний поиск: {option.message}
              </Alert>
            </li>
          );
        }

        let key: string;
        if (option.type === 'local') key = option.book.id;
        else if (option.type === 'external') key = `${option.book.title}-${option.book.author}`;
        else key = `create-${option.title}`;

        const cover =
          option.type === 'local'
            ? option.book.coverUrl
            : option.type === 'external'
              ? option.book.coverUrl
              : null;

        const isCreate = option.type === 'create';
        const liStyle: React.CSSProperties = {
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          ...(isCreate && {
            borderTop: '1px solid rgba(255,255,255,0.12)',
            marginTop: 4,
            paddingTop: 8,
            backgroundColor: 'rgba(255,255,255,0.04)',
            fontWeight: 600,
          }),
        };

        return (
          <li {...props} key={key} style={liStyle}>
            {cover && (
              <img
                src={cover}
                alt={option.type === 'local' ? option.book.title : option.type === 'external' ? option.book.title : 'Обложка'}
                style={{ width: 32, height: 48, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
              />
            )}
            <span>{getOptionDisplayLabel(option)}</span>
          </li>
        );
      }}
      isOptionEqualToValue={() => false}
    />
  );
}
